import React, { useCallback, useState, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { Form, Input, Typography } from 'antd'
import { customList } from 'country-codes-list'
import cn from 'classnames'

import styles from './phone-number.module.scss'

const { Text } = Typography

const PhoneNumber = () => {
  const { formatMessage } = useIntl()
  const form = Form.useFormInstance()
  const [showEdit, setEditPhoneNumber] = useState(false)

  const contactPhoneNumber = Form.useWatch('contact_phone_number', { preserve: true })

  // Generate country codes mapping
  const codes = useMemo(() => {
    const countryCodes = customList('countryNameEn', '+{countryCallingCode}')
    const codesMap = {}
    Object.entries(countryCodes).forEach(([name, code]) => {
      if (!codesMap[code]) {
        codesMap[code] = [name]
      } else {
        codesMap[code].push(name)
      }
    })
    return codesMap
  }, [])

  const handleBlur = useCallback(() => {
    form
      .validateFields(['contact_phone_number'])
      .then(() => {
        setEditPhoneNumber(false)
      })
      .catch(() => {
        // Keep edit mode if there are validation errors
      })
  }, [form])

  const formattedPhoneNumber = useMemo(() => {
    if (!contactPhoneNumber) {
      return ['']
    }

    // Jika phoneNumber sudah mengandung +, pisahkan country code berdasarkan codes prop
    if (contactPhoneNumber.startsWith('+') && codes) {
      // Sort keys berdasarkan panjang (terpanjang dulu) untuk handle kasus seperti "+1 242" vs "+1"
      const sortedCodes = Object.keys(codes).sort(
        (a, b) => b.replace(/\s/g, '').length - a.replace(/\s/g, '').length,
      )

      // Cari country code yang match dengan prefix phoneNumber
      const matchingCode = sortedCodes.find((code) => {
        const codeWithoutSpace = code.replace(/\s/g, '')
        return contactPhoneNumber.startsWith(codeWithoutSpace)
      })

      if (matchingCode) {
        const codeWithoutSpace = matchingCode.replace(/\s/g, '')
        const remainingNumber = contactPhoneNumber.slice(codeWithoutSpace.length)
        return [matchingCode, remainingNumber]
      }
    }

    return [contactPhoneNumber]
  }, [contactPhoneNumber, codes])

  return (
    <div className={styles.phoneNumberSection}>
      <div className={styles.phoneLabel}>
        <span className={styles.required}>*</span>
        {formatMessage({ id: 'financeInvoices.whatsapp_destination_number' })}
      </div>
      {!showEdit ? (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div onClick={() => setEditPhoneNumber(true)} className={styles.phoneDisplay}>
          <Text className={cn(styles.countryCode, { [styles.empty]: !formattedPhoneNumber[0] })}>
            {formattedPhoneNumber[0] || formatMessage({ id: 'sendEmail.enter_phone_number' })}
          </Text>
          {formattedPhoneNumber[1] && (
            <Text className={styles.phoneNumberText}>{formattedPhoneNumber[1]}</Text>
          )}
        </div>
      ) : (
        <Form.Item
          name="contact_phone_number"
          rules={[
            {
              pattern: /^\+?[0-9]*$/,
              message: formatMessage({ id: 'financeInvoices.phone_number_invalid' }),
            },
            {
              min: 10,
              message: formatMessage({ id: 'financeInvoices.phone_number_min' }, { min: 10 }),
            },
            {
              max: 20,
              message: formatMessage({ id: 'financeInvoices.phone_number_max' }, { max: 20 }),
            },
          ]}
        >
          <Input
            placeholder="82211009090"
            className={styles.phoneInput}
            autoFocus
            maxLength={20}
            onBlur={handleBlur}
          />
        </Form.Item>
      )}
      {/* TODO: CHANGE THIS TEXT */}
      <Text className={styles.phoneHelperText}>
        {formatMessage({ id: 'financeInvoices.phone_number_helper_text' })}
      </Text>
    </div>
  )
}

export default PhoneNumber
